import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shouldShowFreeBanner } from '@/access/access';
import { useAccess } from '@/access/useAccess';
import { humanizeError } from '@/api/errors';
import type { PlanSlug, SearchMode, VideoReference } from '@/api/types';
import { track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';
import { usePaywall } from '@/billing/PaywallContext';
import { useSubscriptionPlans } from '@/billing/useSubscriptionPlans';
import { appConfig, storageKeys } from '@/config/app';
import { mediaQuery } from '@/config/breakpoints';
import { useVideoDetail } from '@/features/video/VideoDetailContext';
import { useAddToCanvasPicker } from '@/library/AddToCanvasPicker';
import { useTranslator } from '@/i18n/LocaleProvider';
import { openCrispChat } from '@/integrations/crisp';
import { useBookmarkMutations, useBookmarks } from '@/library/useBookmarks';
import { useCanvasActions, useCanvases } from '@/library/useCanvases';
import { cx } from '@/lib/cx';
import { local, session } from '@/lib/storage';
import { useInView } from '@/lib/useInView';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { CardActionSheet } from './CardActionSheet';
import styles from './Catalog.module.css';
import {
  ActiveFilters,
  CapBlock,
  CategoryHint,
  EmptyState,
  ErrorBlock,
  FreeBanner,
  InterpretedAs,
  LoadingLabel,
  LongPressHint,
  NoExactBanner,
  ZoneHeading,
} from './CatalogBlocks';
import { useCatalogPrefs } from './catalogPrefs';
import { FilterChips } from './FilterChips';
import { countActiveFilters } from './filterModel';
import { PaywallDock } from './PaywallDock';
import { SearchBar } from './SearchBar';
import { SelectionTray } from './SelectionTray';
import { freshTrayVideos, toggleTraySelection } from './traySelection';
import { useCatalogUrlState } from './useCatalogUrlState';
import { useFilterGroups } from './useFilterGroups';
import { useFilterOptions } from './useFilterOptions';
import { useVideos } from './useVideos';
import { VideoGrid } from './VideoGrid';
import { ViewPopover } from './ViewPopover';

/** Каталог (CAT-1…16): поиск, фильтры, сетка, действия карточек, free-tier. */
export function CatalogPage() {
  const { t } = useTranslator();
  const { user } = useAuth();
  const access = useAccess();
  const { openPaywall } = usePaywall();
  const { openVideo, setNavigationList, videoId: openVideoId } = useVideoDetail();
  const { openPicker } = useAddToCanvasPicker();
  const isMobile = useMediaQuery(mediaQuery.mobile);
  const isTablet = useMediaQuery(mediaQuery.tablet);

  const { filters, query, setQuery, setGroup, resetFilters, showCategory } = useCatalogUrlState();
  const { layout, size, setLayout, setSize } = useCatalogPrefs();
  const { data: options } = useFilterOptions();
  const { groups, active } = useFilterGroups(filters, options, setGroup);
  const activeCount = countActiveFilters(filters);
  // Режим запроса — не в адресе: открытая ссылка с `?q=` всегда «отправлена» (ADR-009, D6).
  const [searchMode, setSearchMode] = useState<SearchMode>('submitted');
  const onSearchChange = useCallback(
    (next: string, mode: SearchMode) => {
      setSearchMode(mode);
      setQuery(next);
    },
    [setQuery],
  );
  const result = useVideos(filters, query, searchMode);
  const { videos, ids } = result;

  // Зоны поиска v3: точные совпадения идут первыми, близкие по смыслу — следом; без зон — один список.
  const exactVideos = useMemo(
    () => (result.exactLoaded === null ? videos : videos.slice(0, result.exactLoaded)),
    [videos, result.exactLoaded],
  );
  const closeVideos = useMemo(
    () => (result.exactLoaded === null ? [] : videos.slice(result.exactLoaded)),
    [videos, result.exactLoaded],
  );
  const zoned = result.zones !== null && result.effectiveQuery.length > 0;
  const bothZones = zoned && exactVideos.length > 0 && closeVideos.length > 0;
  const onlyClose = zoned && exactVideos.length === 0 && closeVideos.length > 0;
  const categoryHint =
    result.categoryHint && !filters.category_ids.includes(result.categoryHint.id)
      ? result.categoryHint
      : null;

  const { data: bookmarks } = useBookmarks();
  const bookmarkSet = useMemo(() => bookmarks ?? new Set<number>(), [bookmarks]);
  const { setBookmark, setMany } = useBookmarkMutations();
  const { data: canvases = [] } = useCanvases();
  const { addToCanvas } = useCanvasActions();
  const prices = useSubscriptionPlans();

  // ——— Баннер бесплатного плана (CAT-13) ———
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBanner = shouldShowFreeBanner(access, user) && !bannerDismissed;

  // ——— Выбор в трей ———
  // Выбор не зависит от выдачи: новый поиск не прячет выбранное (traySelection.ts).
  const [tray, setTray] = useState<VideoReference[]>([]);
  const trayVideos = useMemo(() => freshTrayVideos(tray, videos), [tray, videos]);
  const trayIds = useMemo(() => tray.map((v) => v.id), [tray]);
  const traySet = useMemo(() => new Set(trayIds), [trayIds]);
  const toggleTray = useCallback(
    (id: number) => setTray((prev) => toggleTraySelection(prev, id, videos)),
    [videos],
  );

  // ——— Мобильный action sheet и подсказка ———
  const [sheetVideo, setSheetVideo] = useState<VideoReference | null>(null);
  const [hintHidden, setHintHidden] = useState(() => !!local.get(storageKeys.longPressHint));
  const hideHint = useCallback(() => {
    local.set(storageKeys.longPressHint, '1');
    setHintHidden(true);
  }, []);
  const onLongPress = useCallback(
    (video: VideoReference) => {
      setSheetVideo(video);
      if (!hintHidden) hideHint();
    },
    [hintHidden, hideHint],
  );

  // ——— Панель разбора: список для ‹ › и открытие (CAT-15, VID-4) ———
  useEffect(() => {
    setNavigationList(ids);
  }, [ids, setNavigationList]);

  const open = useCallback(
    (video: VideoReference, index: number) => {
      setSheetVideo(null);
      openVideo({
        id: video.id,
        source: 'grid',
        position: result.effectiveQuery ? index : undefined,
        searchEventId: result.effectiveQuery ? result.searchEventId : null,
        searchQuery: result.effectiveQuery || undefined,
      });
    },
    [openVideo, result.effectiveQuery, result.searchEventId],
  );

  // ——— Канвасы (CNV-20) ———
  const addVideoToCanvas = useCallback(
    (
      video: VideoReference,
      canvasId: number,
      alreadyPresent: boolean,
      source: 'grid' | 'sheet',
    ) => {
      setSheetVideo(null);
      if (alreadyPresent) return;
      void addToCanvas(canvasId, [video.id], source, result.searchEventId);
    },
    [addToCanvas, result.searchEventId],
  );
  const newCanvasFor = useCallback(
    (videoIds: number[], source: 'grid' | 'sheet' | 'tray') => {
      setSheetVideo(null);
      openPicker(videoIds, source, result.searchEventId);
    },
    [openPicker, result.searchEventId],
  );

  // ——— Аналитика поиска (CAT-1, CAT-14): восстановление из адреса поиском не считается (CAT-4) ———
  const initialQuery = useRef(result.effectiveQuery);
  const trackedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!result.effectiveQuery || result.isLoading || result.isFetching) return;
    // Набор на лету поиском не считается: событие уходит после паузы или Enter.
    if (result.searchMode === 'typing') return;
    const key = `${result.effectiveQuery}|${result.searchEventId ?? ''}`;
    if (trackedKey.current === key) return;
    trackedKey.current = key;
    if (result.effectiveQuery === initialQuery.current) return;
    track('search_query', {
      metadata: {
        query: result.effectiveQuery,
        source: 'catalog',
        result_count: result.totalUncapped ?? result.total,
        search_event_id: result.searchEventId ?? undefined,
      },
    });
    if (result.total === 0)
      track('search_zero_results_shown', {
        metadata: {
          search_query: result.effectiveQuery,
          search_event_id: result.searchEventId ?? undefined,
        },
      });
  }, [
    result.effectiveQuery,
    result.isLoading,
    result.isFetching,
    result.searchEventId,
    result.searchMode,
    result.total,
    result.totalUncapped,
  ]);

  // ——— Пустая выдача: предложение показывается раз за сессию вкладки (CAT-14) ———
  // Пока выдача меняется (в том числе ждём полную фазу поиска после пустой быстрой), пустоты ещё нет.
  const isEmpty =
    !result.isLoading &&
    !result.isRefreshing &&
    !result.error &&
    videos.length === 0 &&
    (result.effectiveQuery.length > 0 || activeCount > 0 || result.total === 0);
  useEffect(() => {
    if (!isEmpty || result.isFetching || !result.effectiveQuery) return;
    if (session.get(storageKeys.emptyRequestShown)) return;
    session.set(storageKeys.emptyRequestShown, '1');
    track('empty_results_reference_request_banner_shown', {
      metadata: { query: result.effectiveQuery },
    });
  }, [isEmpty, result.isFetching, result.effectiveQuery]);
  const requestReference = () => {
    const hasCrisp = openCrispChat(result.effectiveQuery);
    track('empty_results_reference_request_banner_opened', {
      metadata: { query: result.effectiveQuery, has_crisp: hasCrisp },
    });
    if (!hasCrisp)
      window.location.href = `mailto:${appConfig.supportEmail}?body=${encodeURIComponent(result.effectiveQuery)}`;
  };

  // ——— Бесконечная прокрутка (CAT-7) ———
  const sentinelRef = useRef<HTMLDivElement>(null);
  const sentinelInView = useInView(
    sentinelRef,
    `${appConfig.catalogPrefetchPx}px 0px`,
    result.hasNextPage,
  );
  useEffect(() => {
    if (!sentinelInView || !result.hasNextPage || result.isFetchingNextPage || result.isLoading)
      return;
    track('infinite_scroll_load_more', {
      metadata: {
        current_items_count: videos.length,
        page: Math.floor(videos.length / appConfig.catalogPageSize) + 1,
      },
    });
    result.fetchNextPage();
  }, [sentinelInView, result, videos.length]);

  // ——— Лимит free-tier и док (CAT-12) ———
  // Блок лимита — только когда за капом действительно что-то скрыто: по total_uncapped, а без него по total >= cap.
  const hidden =
    result.totalUncapped !== null ? Math.max(0, result.totalUncapped - videos.length) : null;
  const hasHidden =
    hidden !== null
      ? hidden > 0
      : result.freeTierCap !== null && result.total >= result.freeTierCap;
  const capped =
    result.subscriptionRequired &&
    !result.hasNextPage &&
    !result.isFetching &&
    videos.length > 0 &&
    videos.length >= result.showMoreThreshold &&
    hasHidden;
  const capRef = useRef<HTMLDivElement>(null);
  const capInView = useInView(capRef, '0px 0px -40px 0px', capped);
  const [dockDismissed, setDockDismissed] = useState(
    () => !!session.get(storageKeys.paywallDockDismissed),
  );
  const [dockSeen, setDockSeen] = useState(false);
  useEffect(() => {
    if (capInView) setDockSeen(true);
  }, [capInView]);
  const dockOpen = capped && dockSeen && !dockDismissed && tray.length === 0;
  const dismissDock = () => {
    session.set(storageKeys.paywallDockDismissed, '1');
    setDockDismissed(true);
  };
  const showMore = () => openPaywall({ trigger: 'tripwire', source: 'show_more' });
  const checkout = (plan: PlanSlug) =>
    openPaywall({ trigger: 'intent', source: 'paywall_dock', plan });

  // ——— Мобильный шит фильтров поднимает тулбар над затемнением ———
  const [popOpen, setPopOpen] = useState(false);

  // Числа о размере каталога наружу не выходят (ADR-013): строка называет запрос, а не количество.
  const countLabel =
    result.isLoading || !result.effectiveQuery
      ? ''
      : t('catalog.referencesFor', { query: result.effectiveQuery });

  return (
    <section className={styles.page} aria-label={t('nav.catalog')}>
      {showBanner ? (
        <FreeBanner
          onUpgrade={() => openPaywall({ trigger: 'intent', source: 'free_banner' })}
          onDismiss={() => setBannerDismissed(true)}
        />
      ) : null}

      <div className={cx(styles.toolbar, popOpen && isTablet && styles.toolbarRaised)}>
        <div className={styles.toolbarRow}>
          <SearchBar value={query} onChange={onSearchChange} />
          <ViewPopover layout={layout} size={size} onLayout={setLayout} onSize={setSize} />
        </div>
        <FilterChips
          groups={groups}
          activeCount={activeCount}
          isSheet={isTablet}
          onReset={resetFilters}
          onOpenChange={setPopOpen}
        />
        {result.isRefreshing || result.isLoading ? (
          <span className={styles.progress} aria-hidden="true" />
        ) : null}
      </div>

      <div className={styles.gap} />
      <ActiveFilters chips={active} />
      <p className={styles.count} aria-live="polite">
        {countLabel}
      </p>
      {result.interpretedAs && result.effectiveQuery ? (
        <InterpretedAs value={result.interpretedAs} />
      ) : null}
      {categoryHint ? (
        <CategoryHint
          name={categoryHint.name}
          onShow={() => {
            setSearchMode('submitted');
            showCategory(categoryHint.id);
          }}
        />
      ) : null}
      {isMobile && !hintHidden && videos.length > 0 ? <LongPressHint onDismiss={hideHint} /> : null}

      {result.error ? (
        <ErrorBlock message={humanizeError(result.error)} onRetry={result.refetch} />
      ) : null}

      <div
        key={result.swapKey}
        className={cx(styles.results, result.isRefreshing && styles.resultsRefreshing)}
        aria-busy={result.isRefreshing || undefined}
      >
        {onlyClose ? <NoExactBanner query={result.effectiveQuery} /> : null}
        {bothZones ? <ZoneHeading label={t('catalog.zoneExact')} /> : null}
        {exactVideos.length > 0 ? (
          <VideoGrid
            videos={exactVideos}
            layout={layout}
            size={size}
            bookmarks={bookmarkSet}
            selected={traySet}
            canvases={canvases}
            openVideoId={openVideoId}
            onOpen={open}
            onToggleSelect={toggleTray}
            onAddToCanvas={(video, canvasId, already) =>
              addVideoToCanvas(video, canvasId, already, 'grid')
            }
            onNewCanvas={(video) => newCanvasFor([video.id], 'grid')}
            onLongPress={onLongPress}
          />
        ) : null}
        {bothZones || onlyClose ? <ZoneHeading label={t('catalog.zoneClose')} /> : null}
        {closeVideos.length > 0 ? (
          <VideoGrid
            videos={closeVideos}
            indexOffset={exactVideos.length}
            layout={layout}
            size={size}
            bookmarks={bookmarkSet}
            selected={traySet}
            canvases={canvases}
            openVideoId={openVideoId}
            onOpen={open}
            onToggleSelect={toggleTray}
            onAddToCanvas={(video, canvasId, already) =>
              addVideoToCanvas(video, canvasId, already, 'grid')
            }
            onNewCanvas={(video) => newCanvasFor([video.id], 'grid')}
            onLongPress={onLongPress}
          />
        ) : null}

        {result.isLoading || (result.isRefreshing && videos.length === 0) ? (
          <LoadingLabel text={t('common.loading')} />
        ) : null}
        {isEmpty && !result.error ? (
          <EmptyState
            query={result.effectiveQuery}
            didYouMean={result.didYouMean}
            onPickSuggestion={(next) => onSearchChange(next, 'submitted')}
            onRequest={requestReference}
          />
        ) : null}
      </div>

      {capped ? (
        <CapBlock innerRef={capRef} cap={result.freeTierCap} onShowMore={showMore} />
      ) : null}
      {result.isFetchingNextPage ? <LoadingLabel text={t('common.loadingMore')} /> : null}
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />

      <SelectionTray
        videos={trayVideos}
        canvases={canvases}
        onSave={() => {
          setMany(
            trayIds.filter((id) => !bookmarkSet.has(id)),
            true,
          );
          setTray([]);
        }}
        onAddToCanvas={(canvasId) => {
          const toAdd = trayVideos
            .filter((v) => !v.saved_canvas_ids.includes(canvasId))
            .map((v) => v.id);
          if (toAdd.length) void addToCanvas(canvasId, toAdd, 'tray', result.searchEventId);
          setTray([]);
        }}
        onNewCanvas={() => {
          newCanvasFor(trayIds, 'tray');
          setTray([]);
        }}
        onClear={() => setTray([])}
      />

      <CardActionSheet
        video={sheetVideo}
        saved={sheetVideo ? bookmarkSet.has(sheetVideo.id) : false}
        canvases={canvases}
        onClose={() => setSheetVideo(null)}
        onToggleSave={(video) => {
          setBookmark(video.id, !bookmarkSet.has(video.id));
          setSheetVideo(null);
        }}
        onAddToCanvas={(video, canvasId, already) =>
          addVideoToCanvas(video, canvasId, already, 'sheet')
        }
        onNewCanvas={(video) => newCanvasFor([video.id], 'sheet')}
        onOpen={(video) => open(video, ids.indexOf(video.id))}
      />

      {dockOpen ? (
        <PaywallDock prices={prices} onCheckout={checkout} onDismiss={dismissDock} />
      ) : null}
    </section>
  );
}
